import { MinusOutlined, PlusOutlined, SwapOutlined } from "@ant-design/icons";
import { Button, Dropdown, Flex, Form, Input, MenuProps, Popconfirm, message } from "antd";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useGetProfileMutation, useResetPasswordMutation } from "src/app/services/users";
import { changeCollapsed } from "src/app/slices/layoutSlice";
import { logout } from "src/app/slices/usersSlice";
import { useAppDispatch, useTypedSelector } from "src/app/store";
import { UserIcon, UserSvg } from "src/assets/svg";
import { CloseBarSvg, OpenBarSvg } from "src/assets/svg/homeIcons";
import { LogOutIcon } from "src/assets/svg/menuIcons";
import CustomButton from "src/components/common/button";
import { isMobile } from "src/static/constants";
import DarkMode from "./components/darkMode";
import SelectLang from "./components/selectLangs";
import "./header.scss";
import { JournalActionType, changeJournalAction } from "src/app/slices/journalSlice";
import { useTranslation } from "react-i18next";
import { useGetBranchesMutation, useSelectBranchesMutation } from "src/app/services/management";
import { IBranchRes } from "src/app/services/management/type";
import { saveBranches } from "src/app/slices/branches";
import { Bank, GlobalRefresh, Logout, Refresh } from "iconsax-react";
import { position } from "src/app/services/users/const";
import CustomModal from "src/components/common/modal";
import ModalHeader from "src/components/common/modal/ModalHeader";
import ModalFooter from "src/components/common/modal/ModalFooter";
import { IResetPasswordDto } from "src/app/services/users/type";

function Header() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const branches = useTypedSelector((state) => state.branch.branchs);
  const [getProfile, { data, error }] = useGetProfileMutation();
  const navigate = useNavigate();
  const errorRes: any = error;
  const { profile } = useTypedSelector((state) => state.users);
  const userPostion = profile?.position || position;

  const [getBranches, { data: branchesList, isLoading }] = useGetBranchesMutation();
  const [selectBranches] = useSelectBranchesMutation();

  useEffect(() => {
    if (branches.length == 0) {
      getBranches("")
        .unwrap()
        .then((val) => {
          let arr: IBranchRes[] = [];
          val.results?.forEach((item) => {
            arr.push({ ...item, key: item.id });
          });
          dispatch(saveBranches(arr));
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (errorRes?.status === 403) {
      navigate("/");
      dispatch(logout());
      message.warning(t("auth.signIn.reEnter"));
    }
  }, [errorRes]);

  useEffect(() => {
    getProfile();
  }, []);

  const { collapsed, screenMode } = useTypedSelector((state) => state.layout);
  const toggleCollapsed = () => {
    dispatch(changeCollapsed());
  };

  // Reset
  const [form] = Form.useForm();
  const [reset, { isLoading: resetLaoding }] = useResetPasswordMutation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  const onFinish = (val: IResetPasswordDto) => {
    reset(val)
      .unwrap()
      .then(() => {
        message.success(t("auth.reset.success"));
        closeModal();
      });
  };

  // Menu items
  const dropdownItems: MenuProps["items"] = [
    {
      label: (
        <div className="user" onClick={showModal}>
          <div style={{ height: 32 }}>
            <Refresh size="18" />
            <p>{t("auth.reset.title")}</p>
          </div>
        </div>
      ),
      key: "3",
    },
    {
      label: (
        <div className="user">
          <Popconfirm
            title={t("auth.logout.confirm")}
            onConfirm={() => {
              dispatch(logout());
              message.success(t("auth.logout.success"));
              navigate("/");
            }}
            okText={t("common.yes")}
            cancelText={t("common.no")}
          >
            <div style={{ height: 32 }}>
              <Logout size="18" />
              <p>{t("common.logout")}</p>
            </div>
          </Popconfirm>
        </div>
      ),
      key: "1",
    },
  ];

  if (!profile?.parent_company?.is_branch && !["cfo", "operator", "accountant"].includes(String(userPostion))) {
    dropdownItems.unshift({
      key: "2",
      expandIcon: false,
      label: (
        <Flex align="center" gap={10} style={{ height: 32 }}>
          <GlobalRefresh size="18" />
          {t("auth.reset.profile")}
        </Flex>
      ),
      children: [
        ...branches.map((item) => ({
          key: String(item.id),
          label: (
            <Button
              type="link"
              style={{ width: "100%", color: "inherit" }}
              onClick={() => selectBranches({ company: item.id }).then(() => window.location.reload())}
            >
              {item.title}
            </Button>
          ),
        })),
      ],
    });
  }

  const dropdownMobileItems: MenuProps["items"] = [
    {
      label: (
        <div className="user">
          <div>
            <UserIcon />
            <p>
              {data?.first_name} {data?.last_name}
            </p>
          </div>
        </div>
      ),
      key: "0",
    },
    ...dropdownItems,
  ];

  //Journal actions section
  const onCash = (val: JournalActionType) => {
    navigate("/dashboard/cash");
    dispatch(changeJournalAction(val));
  };

  return (
    <div className="header">
      <div className="header-left">
        {collapsed ? (
          <span className="header-trigger" onClick={toggleCollapsed}>
            <CloseBarSvg />
          </span>
        ) : (
          <span className="header-trigger" onClick={toggleCollapsed}>
            <OpenBarSvg />
          </span>
        )}
      </div>

      <div className="header-buttons">
        <CustomButton onClick={() => onCash("income")} className="header-btn-green" height={38}>
          <PlusOutlined /> {t("common.income")}
        </CustomButton>
        <CustomButton onClick={() => onCash("outcome")} className="header-btn-red" height={38}>
          <MinusOutlined /> {t("common.outcome")}
        </CustomButton>
        <Link to="/dashboard/transfer">
          <CustomButton height={38} className="btn-primary">
            <SwapOutlined /> {t("common.transfer")}
          </CustomButton>
        </Link>
      </div>
      <div className="header-right">
        <DarkMode />
        {/* <Currency /> */}
        <SelectLang visible={false} />
        {/* {screenMode === "enter" ? (
          <FullscreenExitOutlined
            onClick={() => dispatch(changeScreenMode("exit"))}
            className="header-right-screen-icon"
          />
        ) : (
          <FullscreenOutlined
            onClick={() => dispatch(changeScreenMode("enter"))}
            className="header-right-screen-icon"
          />
        )} */}
        <Dropdown
          menu={{
            items: isMobile ? dropdownMobileItems : dropdownItems,
            selectable: true,
            defaultSelectedKeys: [String(data?.company?.id)],
          }}
          trigger={["click"]}
        >
          <div className="header-profile">
            <UserSvg />
            <div className="header-profile-info">
              <p>
                {data?.first_name} {data?.last_name}
              </p>
              <span className="line-clamp1">{profile?.company?.title}</span>
            </div>
          </div>
        </Dropdown>
      </div>

      <CustomModal open={isModalOpen} width={400} onCancel={closeModal} centered={true}>
        <ModalHeader title={t("auth.reset.title")} />

        <Form
          form={form}
          name="normal_login"
          className="form-form"
          onFinish={onFinish}
          layout={"vertical"}
          requiredMark={false}
        >
          <Form.Item
            name="old_password"
            label={t("auth.reset.old_password")}
            rules={[{ required: true, message: t("auth.reset.old_password_message") }]}
            style={{ width: "100%" }}
          >
            <Input className="full" />
          </Form.Item>
          <Form.Item
            name="new_password"
            label={t("auth.reset.new_password")}
            hasFeedback
            rules={[{ required: true, message: t("auth.reset.new_password_message") }]}
            style={{ width: "100%" }}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label={t("auth.reset.confirm_password")}
            hasFeedback
            dependencies={["new_password"]}
            style={{ width: "100%" }}
            rules={[
              { required: true, message: t("auth.reset.confirm_password_message") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("new_password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t("auth.reset.confirm_password_message2")));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
          <ModalFooter onCancel={closeModal} okLoading={resetLaoding} />
        </Form>
      </CustomModal>
    </div>
  );
}

export default Header;
